// The brand wizard's step order, and the verification each step needs.
//
// Kept pure (no React, no state) so the navigation rules can be unit-tested —
// they are the part that is easy to get subtly wrong once every step number is
// clickable and the user can arrive at any step from any other.
//
// VERIFICATION POLICY — deliberately narrow. Every field in this wizard is
// OPTIONAL by design: enrichment fills what the owner leaves blank, and a
// setup-needed notification + email covers anything the pipeline truly needs
// later. So an EMPTY field is never a problem and must never block navigation.
// What we do catch is a value the owner typed that nothing downstream can use
// — a colour that isn't a colour, a URL that isn't a URL. Those are silent
// failures otherwise: the brand saves, and the broken value surfaces days
// later as a post with a dead call-to-action.

export const WIZARD_STEPS = [
  { key: "STEP_01", num: "01", cls: "step-01", label: "Social Accounts" },
  { key: "STEP_02", num: "02", cls: "step-02", label: "Brand Info" },
  { key: "STEP_03", num: "03", cls: "step-03", label: "Brand Settings" },
  { key: "STEP_04", num: "04", cls: "step-04", label: "Content Generation" },
  { key: "STEP_05", num: "05", cls: "step-05", label: "Promotion" },
];

export const stepPosition = (key) => WIZARD_STEPS.findIndex((s) => s.key === key);

export function previousStep(key) {
  const i = stepPosition(key);
  return i > 0 ? WIZARD_STEPS[i - 1].key : null;
}

export function nextStep(key) {
  const i = stepPosition(key);
  return i >= 0 && i < WIZARD_STEPS.length - 1 ? WIZARD_STEPS[i + 1].key : null;
}

// 'done' / 'current' / 'todo' by POSITION. A step the user jumped over still
// reads as done — they were free to skip it, and pretending otherwise would
// make the trail lie about where they are.
export function stepState(key, current) {
  const here = stepPosition(current);
  const there = stepPosition(key);
  if (there < 0 || here < 0) return "todo";
  if (there === here) return "current";
  return there < here ? "done" : "todo";
}

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_RE = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(0|1|0?\.\d+|1\.0+)\s*)?\)$/i;

// Mirrors helpers/colorFormat#isValidColor. Duplicated rather than imported so
// this module stays dependency-free and testable on its own; the two are
// pinned together by a test.
export function isColor(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (HEX_RE.test(v)) return true;
  const m = RGB_RE.exec(v);
  if (!m) return false;
  return [m[1], m[2], m[3]].every((n) => Number(n) >= 0 && Number(n) <= 255);
}

// Deliberately lenient: owners paste "prigmar.com" as often as a full URL, and
// rejecting that would be the wizard being pedantic about something it can fix
// itself. What it rejects is text that cannot become a link at all — no dot in
// the host, whitespace inside, or a scheme with nothing after it.
export function looksLikeUrl(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v || /\s/.test(v)) return false;
  const withoutScheme = v.replace(/^https?:\/\//i, "");
  if (!withoutScheme) return false;              // "https://" and nothing else
  const host = withoutScheme.split(/[/?#]/)[0];
  if (!host || !host.includes(".")) return false;
  // A trailing dot, a doubled dot or a dot at the front is not a host.
  return !/^\.|\.\.|\.$/.test(host);
}

const problem = (field, message) => ({ field, message });

// Problems for ONE step. Empty array = nothing to verify.
export function verifyStep(key, values = {}) {
  const v = values || {};
  const found = [];

  if (key === "STEP_02") {
    if (v.website && !looksLikeUrl(v.website)) {
      found.push(problem("website", "Brand Website doesn't look like a web address."));
    }
  }

  if (key === "STEP_03") {
    if (v.theme && !isColor(v.theme)) {
      found.push(problem("theme", "Primary Brand Theme Color isn't a valid hex or rgb(a) colour."));
    }
    if (v.secondaryTheme && !isColor(v.secondaryTheme)) {
      found.push(problem("secondaryTheme", "Secondary Brand Theme Color isn't a valid hex or rgb(a) colour."));
    }
    if (v.actionUrl && !looksLikeUrl(v.actionUrl)) {
      found.push(problem("actionUrl", "Call to Action URL doesn't look like a web address."));
    }
  }

  if (key === "STEP_05") {
    const promo = v.promotion || {};
    if (promo.ctaUrl && !looksLikeUrl(promo.ctaUrl)) {
      found.push(problem("ctaUrl", "Promotion call-to-action URL doesn't look like a web address."));
    }
    // A feature with no title can never be posted — the clip has nothing to
    // announce — and it reaches here from the server as well as the editor.
    const features = Array.isArray(promo.features) ? promo.features : [];
    const untitled = features.filter((f) => f && !String(f.title || "").trim()).length;
    if (untitled > 0) {
      found.push(problem("features", `${untitled} promotion ${untitled === 1 ? "entry has" : "entries have"} no title.`));
    }
  }

  return found;
}

// { STEP_02: [problem, …] } for every step that has something to fix. Steps
// with nothing wrong are absent, so the truthiness of a lookup is the badge.
export function verifyAll(values = {}) {
  const out = {};
  for (const s of WIZARD_STEPS) {
    const found = verifyStep(s.key, values);
    if (found.length) out[s.key] = found;
  }
  return out;
}

// Whether leaving `from` is allowed, and why not.
//
// Going BACK is always allowed even with problems — trapping someone on a step
// because of a typo they may want to look up is how a wizard becomes a maze,
// and the problem stays visible on the stepper either way. Moving FORWARD (or
// jumping ahead) is what verification gates.
export function canLeaveStep(from, to, values = {}) {
  const forward = stepPosition(to) > stepPosition(from);
  if (!forward) return { ok: true, problems: [] };
  const problems = verifyStep(from, values);
  return { ok: problems.length === 0, problems };
}
